import React, { useState, useEffect, useMemo, useCallback, useTransition } from "react";
import {
  Modal,
  Loader,
  Alert as MantineAlert,
  ScrollArea,
  Paper,
  Button,
  Tooltip,
} from "@mantine/core";
import {
  IconHistory,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons-react";
import {
  diff_match_patch,
  DIFF_DELETE,
  DIFF_INSERT,
  DIFF_EQUAL,
} from "diff-match-patch";
import Mixpanel from "../components/Lib/mixpanel";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import { useMediaQuery } from "@mantine/hooks";

// Helper function to strip HTML tags (Remains the same)
const stripHtml = (html) => {
  if (!html || typeof html !== "string") {
    return "";
  }
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc
      .querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6, blockquote, tr")
      .forEach((el) => {
        if (
          !el.nextSibling ||
          el.nextSibling.nodeType !== Node.TEXT_NODE ||
          !el.nextSibling.textContent.startsWith("\n")
        ) {
          el.after(doc.createTextNode("\n"));
        }
      });
    return (doc.body.textContent || "").trim();
  } catch (e) {
    console.error("Error stripping HTML", e);
    return "[Error processing content]";
  }
};

// Helper function to generate readable diff HTML (Increased font size)
const generateDiffHtml = (diffs) => {
  let html = "";
  for (let x = 0; x < diffs.length; x++) {
    const op = diffs[x][0];
    const data = diffs[x][1];
    const text = data
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, "&para;<br>");
    switch (op) {
      case DIFF_INSERT:
        html += `<ins style="background:#dcfce7; color: #15803d; text-decoration: none; padding: 0.1em 0; border-radius: 3px;">${text}</ins>`;
        break;
      case DIFF_DELETE:
        html += `<del style="background:#fee2e2; color: #b91c1c; text-decoration: line-through; padding: 0.1em 0; border-radius: 3px;">${text}</del>`;
        break;
      case DIFF_EQUAL:
        html += `<span>${text}</span>`;
        break;
    }
  }
  // INCREASED FONT SIZE: Use 1rem (text-base equivalent) for diff content
  return `<pre style="white-space: pre-wrap; word-wrap: break-word; margin: 0; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; line-height: 1.7; font-size: 1rem;">${html}</pre>`;
};

// --- Component using Mantine Structure + Tailwind Styling (Larger Fonts) ---
function RevisionHistoryViewer({ startupEntitySlug, subCategoryId }) {
  // State hooks remain the same
  const [modalOpened, setModalOpened] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [isLoadingRevisions, setIsLoadingRevisions] = useState(false);
  const [revisionsError, setRevisionsError] = useState(null);
  const [selectedRevisionId, setSelectedRevisionId] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  const {t}=useTranslation();
  // Inside component
const isMobile = useMediaQuery('(max-width: 768px)');

  // dmp instance remains the same
  const dmp = useMemo(() => new diff_match_patch(), []);

  // Fetch Revisions (Original Logic) - Remains the same
  useEffect(() => {
    // ... fetch logic ...
    if (modalOpened && startupEntitySlug && subCategoryId) {
      const fetchRevisions = async () => {
        setIsLoadingRevisions(true);
        setRevisionsError(null);
        setRevisions([]);
        setSelectedRevisionId(null);
        setComparisonData(null);
        try {
          const response = await fetch(
            `/sub_categories/${subCategoryId}/versions`
          );
          if (!response.ok)
            throw new Error(`HTTP error! status: ${response.status}`);
          const data = await response.json();
          if (!Array.isArray(data))
            throw new Error("Invalid data format received");
          console.log(data);
          setRevisions(
            data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          );
        } catch (error) {
          console.error("Failed to fetch revisions:", error);
          setRevisionsError(`Failed to load revisions: ${error.message}`);
        } finally {
          setIsLoadingRevisions(false);
        }
      };
      fetchRevisions();
    } else {
      setRevisions([]);
      setSelectedRevisionId(null);
      setComparisonData(null);
      setRevisionsError(null);
      setComparisonError(null);
    }
  }, [modalOpened, startupEntitySlug, subCategoryId]);

  // Fetch Comparison (Original Logic) - Remains the same
  useEffect(() => {
    // ... fetch logic ...
    if (selectedRevisionId && startupEntitySlug && subCategoryId) {
      const fetchComparison = async () => {
        setIsLoadingComparison(true);
        setComparisonError(null);
        setComparisonData(null);
        try {
          const response = await fetch(
            `/sub_categories/${subCategoryId}/versions/${selectedRevisionId}/response_comparison_data`
          );
          if (!response.ok) {
            let errorMsg = `HTTP error! status: ${response.status}`;
            try {
              const errorData = await response.json();
              errorMsg = `${errorMsg} - ${
                errorData.error || "Failed to load comparison"
              }`;
            } catch (_) {}
            throw new Error(errorMsg);
          }
          const data = await response.json();
          if (
            typeof data.previous_content === "undefined" ||
            typeof data.current_content === "undefined"
          ) {
            throw new Error("Incomplete comparison data received.");
          }
          setComparisonData(data);
        } catch (error) {
          console.error("Failed to fetch comparison data:", error);
          setComparisonError(`Failed to load comparison: ${error.message}`);
        } finally {
          setIsLoadingComparison(false);
        }
      };
      fetchComparison();
    } else {
      setComparisonData(null);
      setComparisonError(null);
      setIsLoadingComparison(false);
    }
  }, [selectedRevisionId, startupEntitySlug, subCategoryId]);

  // Diff HTML memoization (Remains the same logic, returns strings)
  const diffHtml = useMemo(() => {
    // ... diff logic ...
    if (!comparisonData || !dmp || isLoadingComparison || comparisonError)
      return null;
    try {
      const text1 = stripHtml(comparisonData.previous_content ?? "");
      const text2 = stripHtml(comparisonData.current_content ?? "");
      if (
        text1 === "[Error processing content]" ||
        text2 === "[Error processing content]"
      ) {
        return "Error processing content for comparison.";
      }
      if (text1 === text2) {
        return "No textual changes detected between these versions.";
      }
      const diffs = dmp.diff_main(text1, text2);
      dmp.diff_cleanupSemantic(diffs);
      return generateDiffHtml(diffs); // Uses the updated function with larger font size
    } catch (error) {
      console.error("Error generating diff:", error);
      return `Error generating diff view.`;
    }
  }, [comparisonData, dmp, isLoadingComparison, comparisonError]);

  // handleRevisionClick callback remains the same
  const handleRevisionClick = useCallback((revisionId) => {
    setSelectedRevisionId((prevId) =>
      prevId === revisionId ? null : revisionId
    );
  }, []);

  // formatTimestamp utility remains the same
  const formatTimestamp = (isoString) => {
    // ... format logic ...
    if (!isoString) return "N/A";
    try {
      return new Date(isoString).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Invalid Date";
    }
  };

  // Determine if the diffHtml is informational or actual diff (Remains the same)
  const isDiffMessage =
    diffHtml === "Error processing content for comparison." ||
    diffHtml === "No textual changes detected between these versions." ||
    diffHtml === "Error generating diff view.";

  return (
    <div className="ml-auto">
      {/* --- Trigger Button (Larger Font) --- */}

      <Tooltip
        label={t("startups_page.changes")}
        position="bottom"
        withArrow
        color="#6c45e7"
        transitionProps={{ transition: "pop", duration: 300 }}
      >
        <span
          style={{
            display: "inline-block",
            cursor: (!startupEntitySlug || !subCategoryId) ? "not-allowed" : undefined,
          }}
        >
          <Button
            size="xs"
            variant="light"
            color="#6c45e7"
            onClick={() => {
              setModalOpened(true);
              // Mixpanel tracking for button click
              Mixpanel.button('View Editor Changes', {
                location: 'Editor Page',
                startup_entity_slug: startupEntitySlug,
                subcategory_id: subCategoryId,
              });
            }}
            disabled={!startupEntitySlug || !subCategoryId}
            aria-label="View Changes"
            style={(!startupEntitySlug || !subCategoryId) ? { pointerEvents: "none" } : {}}
          >
            <IconHistory size={16} />
          </Button>
        </span>
      </Tooltip>

      {/* --- Modal Structure (Mantine) --- */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        size={isMobile ? "100%" : "85%"} // Full width on mobile
        fullScreen={isMobile} // Full screen on mobile
        padding={0}
        withCloseButton
        title={
          // INCREASED FONT SIZE: text-xl
          <h2
            className="text-xl font-semibold text-gray-800 px-4 py-3"
            id="revision-modal-title"
          >
            Revision History
          </h2>
        }
        styles={{
          header: { margin: 0, borderBottom: "1px solid #e5e7eb" },
          body: { padding: 0, height: "80vh", display: "flex" }, // Increased height slightly
        }}
        trapFocus
        closeOnEscape
        closeOnClickOutside
        aria-labelledby="revision-modal-title"
      >
        {/* --- Layout within Modal Body (Tailwind) --- */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* --- Left Panel: Revision List --- */}
          {/* INCREASED WIDTH: max-w-sm */}
          <div className="w-full md:w-1/3 md:max-w-sm flex flex-col border-r border-gray-200 bg-gray-50">
            {/* INCREASED FONT SIZE: text-base */}
            <h3 className="text-base font-semibold text-gray-700 px-4 py-3 border-b border-gray-200 flex-shrink-0 bg-white">
              Revisions
            </h3>
            <ScrollArea className="flex-1" type="auto">
              {/* INCREASED PADDING/SPACING */}
              <div className="p-4 space-y-3">
                {isLoadingRevisions && (
                  <div className="flex justify-center items-center py-8">
                    <Loader size="sm" />
                  </div>
                )}
                {revisionsError && (
                  <MantineAlert
                    icon={<IconAlertCircle size={18} />}
                    title="Error Loading Revisions"
                    color="red"
                    radius="md"
                    className="mx-1"
                  >
                    {/* INCREASED FONT SIZE: text-base */}
                    <p className="text-base">{revisionsError}</p>
                  </MantineAlert>
                )}
                {!isLoadingRevisions &&
                  !revisionsError &&
                  revisions.length === 0 && (
                    // INCREASED FONT SIZE: text-base
                    <p className="text-base text-gray-500 text-center py-8 px-3">
                      No revisions found.
                    </p>
                  )}
                {revisions.map((rev) => (
                  // INCREASED PADDING
                  <button
                    key={rev.id}
                    type="button"
                    onClick={() => handleRevisionClick(rev.id)}
                    className={`w-full text-left p-4 rounded-md border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${
                      selectedRevisionId === rev.id
                        ? "bg-blue-100 border-blue-400 shadow-sm text-blue-900"
                        : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-gray-800"
                    }`}
                  >
                    {/* INCREASED FONT SIZES: text-base, text-sm */}
                    <p className="text-base font-medium truncate">
                      Revision {rev.id}{" "}
                      {rev.event ? (
                        <span className="text-sm font-normal text-gray-500">
                          ({rev.event})
                        </span>
                      ) : (
                        ""
                      )}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatTimestamp(rev.timestamp)}
                    </p>
                    <p className="text-sm text-gray-600 truncate mt-1">
                      By:{" "}
                      <span className="font-medium text-gray-700">
                        {rev.contributor_name || "Unknown"}
                      </span>
                    </p>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* --- Right Panel: Diff Viewer --- */}
          <div className="flex-1 flex flex-col bg-white">
            {/* INCREASED FONT SIZE: text-base */}
            <h3 className="text-base font-semibold text-gray-700 px-6 py-3 border-b border-gray-200 flex-shrink-0">
              Changes in Selected Revision
            </h3>
            <ScrollArea className="flex-1" type="auto">
              {/* INCREASED PADDING */}
              <div className="p-6">
                {isLoadingComparison && (
                  <div className="flex justify-center items-center py-8">
                    <Loader size="md" />
                  </div>
                )}
                {comparisonError && (
                  <MantineAlert
                    icon={<IconAlertCircle size={18} />}
                    title="Error Loading Comparison"
                    color="red"
                    radius="md"
                  >
                    {/* INCREASED FONT SIZE: text-base */}
                    <p className="text-base">{comparisonError}</p>
                  </MantineAlert>
                )}
                {!selectedRevisionId &&
                  !isLoadingRevisions &&
                  !isLoadingComparison &&
                  !comparisonError && (
                    <MantineAlert
                      icon={<IconInfoCircle size={18} />}
                      title="Select Revision"
                      color="blue"
                      variant="outline"
                      radius="md"
                    >
                      {/* INCREASED FONT SIZE: text-base */}
                      <p className="text-base leading-relaxed">
                        Click on a revision from the list on the left to view
                        the changes made in that version compared to the
                        previous one.
                      </p>
                    </MantineAlert>
                  )}
                {comparisonData && !isLoadingComparison && !comparisonError && (
                  // INCREASED MARGIN
                  <Paper
                    withBorder
                    radius="md"
                    className="mt-5 overflow-hidden shadow-sm"
                  >
                    {/* INCREASED FONT SIZE: text-sm */}
                    <div className="flex flex-wrap justify-between items-center bg-gray-50 px-4 py-2.5 border-b border-gray-200 text-sm text-gray-600 gap-x-4 gap-y-1">
                      <p>
                        Changes by:{" "}
                        <span className="font-medium text-gray-800">
                          {comparisonData.contributor_name || "Unknown"}
                        </span>
                      </p>
                      <p>
                        Timestamp:{" "}
                        <span className="font-medium text-gray-800">
                          {formatTimestamp(comparisonData.timestamp)}
                        </span>
                      </p>
                    </div>
                    {isDiffMessage ? (
                      <MantineAlert
                        color={diffHtml.includes("Error") ? "red" : "blue"}
                        radius={0}
                        className="border-0 border-t border-gray-200"
                        icon={
                          diffHtml.includes("Error") ? (
                            <IconAlertCircle size={18} />
                          ) : (
                            <IconInfoCircle size={18} />
                          )
                        }
                      >
                        {/* INCREASED FONT SIZE: text-base */}
                        <p className="text-base">{diffHtml}</p>
                      </MantineAlert>
                    ) : (
                      // INCREASED PADDING & FONT SIZE (via generateDiffHtml)
                      <div
                        key={selectedRevisionId}
                        className="p-5" // Increased padding around diff
                        dangerouslySetInnerHTML={{ __html: diffHtml || "" }}
                      />
                    )}
                  </Paper>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default React.memo(RevisionHistoryViewer);
